import { type AgentSessionResponse } from '@air/server-sdk';

import { splitPillMonikers } from '~/components/v2/components/workspace/workspace-agent-panel/utils/splitPillMonikers';
import { WORKSPACE_AGENT_SESSION_ROW_PREVIEW_MAX_LENGTH } from '~/components/v2/components/workspace/workspace-agent-panel/workspaceAgentPanelConstants';

const UNCLOSED_MARKDOWN_DELIMITERS = ['**', '__', '~~'] as const;

const isWordChar = (character: string | undefined): boolean =>
  character !== undefined && /[\p{L}\p{N}_]/u.test(character);

const isDigit = (character: string | undefined): boolean => character !== undefined && /\d/u.test(character);

const isSingleAsteriskEmphasisDelimiter = (text: string, index: number): boolean => {
  const previous = text[index - 1];
  const next = text[index + 1];

  if (isDigit(previous) && isDigit(next)) {
    return false;
  }

  if (next !== undefined && /\s/u.test(next)) {
    const restAfterWhitespace = text.slice(index + 1).trimStart();

    if (/^[^\s*]+:\*/u.test(restAfterWhitespace)) {
      return true;
    }

    const isListMarkerBoundary = index === 0 || previous === undefined || /[\s:([{-]/u.test(previous);

    if (isListMarkerBoundary) {
      return false;
    }
  }

  return true;
};

const isSingleUnderscoreEmphasisDelimiter = (text: string, index: number): boolean => {
  const previous = text[index - 1];
  const next = text[index + 1];

  return !(isWordChar(previous) && isWordChar(next));
};

const stripFromLastUnclosedSingleDelimiter = (
  text: string,
  delimiter: string,
  isEmphasisDelimiter: (value: string, index: number) => boolean,
): string => {
  const doubled = delimiter.repeat(2);
  const singleIndices: number[] = [];

  for (let index = 0; index < text.length; index += 1) {
    if (isInsideBacktickSpan(text, index)) {
      continue;
    }

    if (text.startsWith(doubled, index)) {
      index += doubled.length - 1;
      continue;
    }

    if (text[index] === delimiter && isEmphasisDelimiter(text, index)) {
      singleIndices.push(index);
    }
  }

  if (singleIndices.length % 2 === 1) {
    const lastIndex = singleIndices[singleIndices.length - 1];

    if (lastIndex !== undefined) {
      return text.slice(0, lastIndex);
    }
  }

  return text;
};

const isInsideBacktickSpan = (text: string, index: number): boolean => {
  let inside = false;

  for (let characterIndex = 0; characterIndex < index; characterIndex += 1) {
    if (text[characterIndex] === '`') {
      inside = !inside;
    }
  }

  return inside;
};

const stripFromLastUnclosedBacktick = (text: string): string => {
  const backtickCount = text.split('`').length - 1;

  if (backtickCount % 2 === 1) {
    const lastBacktickIndex = text.lastIndexOf('`');

    if (lastBacktickIndex !== -1) {
      return text.slice(0, lastBacktickIndex);
    }
  }

  return text;
};

const stripFromLastUnclosedMarkdownLink = (text: string): string => {
  let lastOpenBracket = -1;

  for (let index = text.length - 1; index >= 0; index -= 1) {
    if (text[index] === '[' && !isInsideBacktickSpan(text, index)) {
      lastOpenBracket = index;
      break;
    }
  }

  if (lastOpenBracket === -1) {
    return text;
  }

  const segment = text.slice(lastOpenBracket);
  const closeBracketIndex = segment.indexOf(']');

  if (closeBracketIndex === -1) {
    return text.slice(0, lastOpenBracket);
  }

  const afterBracket = segment.slice(closeBracketIndex);

  if (afterBracket.startsWith('](')) {
    const closeParenIndex = afterBracket.indexOf(')');

    if (closeParenIndex === -1) {
      return text.slice(0, lastOpenBracket);
    }
  }

  return text;
};

/**
 * Remove incomplete markdown regions after preview text is cut — either at a pill or by max length.
 * Drops content from the last unclosed opener to the end, then orphaned list prefixes.
 */
const stripUnclosedMarkdownRegions = (text: string): string => {
  let result = text;
  let changed = true;

  while (changed) {
    changed = false;
    const previous = result;

    result = result.trimEnd();

    for (const delimiter of UNCLOSED_MARKDOWN_DELIMITERS) {
      while ((result.split(delimiter).length - 1) % 2 === 1) {
        const lastDelimiterIndex = result.lastIndexOf(delimiter);

        if (lastDelimiterIndex === -1) {
          break;
        }

        result = result.slice(0, lastDelimiterIndex);
        changed = true;
      }
    }

    const withoutUnclosedSingleAsterisk = stripFromLastUnclosedSingleDelimiter(
      result,
      '*',
      isSingleAsteriskEmphasisDelimiter,
    );

    if (withoutUnclosedSingleAsterisk !== result) {
      result = withoutUnclosedSingleAsterisk;
      changed = true;
    }

    const withoutUnclosedSingleUnderscore = stripFromLastUnclosedSingleDelimiter(
      result,
      '_',
      isSingleUnderscoreEmphasisDelimiter,
    );

    if (withoutUnclosedSingleUnderscore !== result) {
      result = withoutUnclosedSingleUnderscore;
      changed = true;
    }

    const withoutUnclosedBacktick = stripFromLastUnclosedBacktick(result);

    if (withoutUnclosedBacktick !== result) {
      result = withoutUnclosedBacktick;
      changed = true;
    }

    const withoutUnclosedLink = stripFromLastUnclosedMarkdownLink(result);

    if (withoutUnclosedLink !== result) {
      result = withoutUnclosedLink;
      changed = true;
    }

    result = result.replace(/\s[-*+]\s*$/u, '');
    result = result.replace(/\s\d+\.\s*$/u, '');

    if (result !== previous) {
      changed = true;
    }
  }

  return result.trim();
};

/** Remove bold markers from preview markdown; italic and other inline styles are kept. */
const stripBoldMarkdown = (text: string): string =>
  text
    .replace(/\*\*\*(.+?)\*\*\*/g, '*$1*')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/__(.+?)__/g, '$1');

/** Strip trailing whitespace and punctuation, but keep markdown delimiters so balanced inline markers survive. */
const stripTrailingNonAlphanumeric = (text: string): string => {
  let result = text.trimEnd();

  while (/[^\p{L}\p{N}*_`~]$/u.test(result)) {
    result = result.slice(0, -1);
  }

  return result;
};

const finalizeTruncatedPreview = ({
  text,
  truncatedAtPill,
  truncatedByLength,
}: {
  text: string;
  truncatedAtPill: boolean;
  truncatedByLength: boolean;
}): string | undefined => {
  const withoutTrailing = stripTrailingNonAlphanumeric(text);

  if (!withoutTrailing) {
    return undefined;
  }

  const strippedTrailingNonAlphanumeric = withoutTrailing.length < text.length;

  if (truncatedByLength || (truncatedAtPill && strippedTrailingNonAlphanumeric)) {
    return `${withoutTrailing}…`;
  }

  return withoutTrailing;
};

/** Text that appears before the first `@[pill:<id>]` moniker, normalized for a single-line preview. */
export const prepareWorkspaceAgentSessionRowPreviewMarkdown = (
  latestAgentMessage: string | undefined,
): string | undefined => {
  if (typeof latestAgentMessage !== 'string' || !latestAgentMessage.trim()) {
    return undefined;
  }

  let textBeforeFirstPill = '';
  let truncatedAtPill = false;

  for (const segment of splitPillMonikers(latestAgentMessage)) {
    if (segment.type === 'pill' || segment.type === 'unknownMoniker') {
      truncatedAtPill = true;
      break;
    }

    if (segment.type === 'text') {
      textBeforeFirstPill += segment.value;
    }
  }

  let normalized = textBeforeFirstPill.replace(/\s+/g, ' ').trim();

  if (!normalized) {
    return undefined;
  }

  let truncatedByLength = false;

  if (normalized.length > WORKSPACE_AGENT_SESSION_ROW_PREVIEW_MAX_LENGTH) {
    normalized = normalized.slice(0, WORKSPACE_AGENT_SESSION_ROW_PREVIEW_MAX_LENGTH - 1);
    truncatedByLength = true;
  }

  if (truncatedAtPill || truncatedByLength) {
    normalized = stripUnclosedMarkdownRegions(normalized);
  }

  if (!normalized) {
    return undefined;
  }

  normalized = stripBoldMarkdown(normalized);

  if (truncatedAtPill || truncatedByLength) {
    return finalizeTruncatedPreview({
      text: normalized,
      truncatedAtPill,
      truncatedByLength,
    });
  }

  return normalized;
};

export const getWorkspaceAgentSessionRowPreviewMarkdown = (session: AgentSessionResponse): string | undefined => {
  return prepareWorkspaceAgentSessionRowPreviewMarkdown(session.latestAgentMessage);
};
